import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ticketsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, showToast } from '../../utils/helpers';
import TicketFormModal from './TicketFormModal';
import { Plus, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const AgencyTicketsPage = () => {
  const { isAdmin } = useAuth();
  const { isRTL, t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchTickets();
    }
  }, [filterStatus, filterCategory, isAdmin]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category = filterCategory;

      const response = await ticketsAPI.getAll(params);
      setTickets(response.data.tickets || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTicket(null);
    setShowModal(true);
  };

  const handleEdit = (ticket) => {
    if (ticket.status === 'open') {
      setEditingTicket(ticket);
      setShowModal(true);
    } else {
      showToast.error(t('onlyOpenTicketsEditable'));
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTicket(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchTickets();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      open: { label: t('statusOpen'), color: 'bg-blue-100 text-blue-800', icon: MessageSquare },
      in_progress: { label: t('statusInProgress'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      resolved: { label: t('statusResolved'), color: 'bg-green-100 text-green-800', icon: CheckCircle },
      closed: { label: t('statusClosed'), color: 'bg-gray-100 text-gray-800', icon: XCircle },
    };
    const statusInfo = statusMap[status] || statusMap.open;
    const Icon = statusInfo.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <Icon className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      technical: t('categoryTechnical'),
      billing: t('categoryBilling'),
      feature_request: t('categoryFeatureRequest'),
      bug: t('categoryBug'),
      other: t('categoryOther'),
    };
    return categoryMap[category] || category;
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      low: { label: t('priorityLow'), color: 'bg-gray-100 text-gray-700' },
      medium: { label: t('priorityMedium'), color: 'bg-blue-100 text-blue-700' },
      high: { label: t('priorityHigh'), color: 'bg-orange-100 text-orange-700' },
      urgent: { label: t('priorityUrgent'), color: 'bg-red-100 text-red-700' },
    };
    const priorityInfo = priorityMap[priority] || priorityMap.medium;
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${priorityInfo.color}`}>
        {priorityInfo.label}
      </span>
    );
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-gray-600">
          {t('accessDenied')}
        </p>
      </div>
    );
  }

  if (loading) {
    return <Loading fullScreen />;
  }

  const statusCounts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-5 h-5 text-sky-600" />
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                {isRTL ? 'تذاكر الدعم' : 'Support Tickets'}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {t('ticketsTitle')}
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              {t('ticketsSubtitle')}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-start md:justify-end">
            <Button
              onClick={handleCreate}
              variant="primary"
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-sm"
            >
              <Plus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('newTicket')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { value: 'all', label: t('all') },
            { value: 'open', label: t('statusOpen') },
            { value: 'in_progress', label: t('statusInProgress') },
            { value: 'resolved', label: t('statusResolved') },
            { value: 'closed', label: t('statusClosed') },
          ].map(status => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                filterStatus === status.value
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {status.label} ({statusCounts[status.value] || 0})
            </button>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <label className={`text-sm font-medium text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('ticketCategory')}:
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white"
          >
            <option value="all">{t('all')}</option>
            <option value="technical">{t('categoryTechnical')}</option>
            <option value="billing">{t('categoryBilling')}</option>
            <option value="feature_request">{t('categoryFeatureRequest')}</option>
            <option value="bug">{t('categoryBug')}</option>
            <option value="other">{t('categoryOther')}</option>
          </select>
        </div>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-4">
              {t('noTickets')}
            </p>
            <Button
              onClick={handleCreate}
              variant="primary"
              className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-sm"
            >
              <Plus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('createNewTicket')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="p-6 hover:shadow-md transition-shadow rounded-2xl border-slate-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-mono text-sm text-gray-500">
                      #{ticket.ticket_number}
                    </span>
                    {getStatusBadge(ticket.status)}
                    {getPriorityBadge(ticket.priority)}
                    <span className="text-sm text-gray-600">
                      {getCategoryLabel(ticket.category)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {ticket.subject}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {ticket.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                    <span>{formatDate(ticket.created_at)}</span>
                    {ticket.assigned_to_name && (
                      <>
                        <span>•</span>
                        <span>
                          {t('ticketAssignedTo')}: {ticket.assigned_to_name}
                        </span>
                      </>
                    )}
                    {ticket.resolved_at && (
                      <>
                        <span>•</span>
                        <span>
                          {t('ticketResolvedAt')}: {formatDate(ticket.resolved_at)}
                        </span>
                      </>
                    )}
                  </div>
                  {ticket.resolution && (
                    <div
                      className={`mt-4 p-3 bg-green-50 border border-green-200 rounded-lg ${
                        isRTL ? 'text-right' : 'text-left'
                      }`}
                    >
                      <p className="text-sm font-medium text-green-900 mb-1">
                        {t('ticketResolution')}:
                      </p>
                      <p className="text-sm text-green-800">{ticket.resolution}</p>
                    </div>
                  )}
                </div>
                {ticket.status === 'open' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(ticket)}
                    className={isRTL ? 'ml-4' : 'mr-4'}
                  >
                    {t('editButton')}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Ticket Form Modal */}
      {showModal && (
        <TicketFormModal
          ticket={editingTicket}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default AgencyTicketsPage;
